import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Youtube, Save, ChevronLeft, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../services/api';

/**
 * Admin-UI: YouTube-URLs pro Lektion setzen.
 *
 * Flow:
 *   1. Modul-Liste (die 10 Micro-Learning-Kurse) laden
 *   2. Klick auf ein Modul -> Lektionsliste + Input-Felder
 *   3. "Alle speichern"-Button -> bulk PATCH ans Backend
 */

const MICRO_LEARNING_MODULES = [
  { id: 'dguv-v3-anlagen-betriebsmittel', title: 'DGUV V3 – Elektrische Anlagen & Betriebsmittel' },
  { id: 'dguv-r-103-011-aus', title: 'DGUV R 103-011 – Arbeiten unter Spannung' },
  { id: 'psa-absturz-leitern-geruste', title: 'PSA gegen Absturz, Leitern & Gerüste' },
  { id: 'erste-hilfe-stromunfall-brand', title: 'Erste Hilfe: Stromunfall & Brand' },
  { id: 'din-en-50173-vde-0800-verkabelung', title: 'DIN EN 50173 / VDE 0800 – Strukturierte Verkabelung' },
  { id: 'knx-smart-home-sicherheit', title: 'KNX & Smart-Home – Bus-Sicherheit' },
  { id: 'pv-speicher-brandschutz-dc-freischaltung', title: 'PV & Speicher – Brandschutz & DC-Freischaltung' },
  { id: 'wallboxen-rcd-typ-b-din-0100-722', title: 'Wallboxen – RCD Typ B & DIN VDE 0100-722' },
  { id: 'baustromverteiler-din-203-006', title: 'Baustromverteiler & DGUV Info 203-006' },
  { id: 'erstpruefung-din-0100-600-0701-0702', title: 'Erst- & Wiederholungsprüfung nach DIN VDE 0100-600 / 0701-0702' },
];

const API = process.env.REACT_APP_BACKEND_URL;

export const LessonVideoUrlManager = () => {
  const [open, setOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [drafts, setDrafts] = useState({}); // {lesson_id: url}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadLessons = useCallback(async (moduleId) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/admin/modules/${moduleId}/lessons`);
      const data = await res.json();
      if (data.success) {
        setLessons(data.lessons || []);
        const d = {};
        (data.lessons || []).forEach((l) => { d[l.id] = l.video_url || ''; });
        setDrafts(d);
      } else {
        toast.error('Konnte Lektionen nicht laden');
      }
    } catch {
      toast.error('Netzwerkfehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedModule) loadLessons(selectedModule.id);
  }, [selectedModule, loadLessons]);

  const handleSaveAll = async () => {
    if (!selectedModule) return;
    setSaving(true);
    try {
      // Nur geänderte URLs mitschicken
      const urls = {};
      lessons.forEach((l) => {
        const d = (drafts[l.id] || '').trim();
        if (d !== (l.video_url || '')) urls[l.id] = d;
      });
      if (Object.keys(urls).length === 0) {
        toast.info('Keine Änderungen zu speichern');
        setSaving(false);
        return;
      }
      const res = await apiFetch(`${API}/api/admin/modules/${selectedModule.id}/bulk-video-urls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.count} Lektion${data.count === 1 ? '' : 'en'} aktualisiert`);
        await loadLessons(selectedModule.id); // refresh
      } else {
        toast.error(data.detail || 'Speichern fehlgeschlagen');
      }
    } catch {
      toast.error('Netzwerkfehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const resetView = () => {
    setSelectedModule(null);
    setLessons([]);
    setDrafts({});
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) resetView();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
          data-testid="open-video-manager-btn"
        >
          <Youtube className="h-4 w-4 mr-2" />
          YouTube-Videos verwalten
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-red-500/30 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="video-manager-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Youtube className="h-5 w-5 text-red-400" />
            {selectedModule ? selectedModule.title : 'Kurs auswählen'}
          </DialogTitle>
          <DialogDescription className="text-gray-300 pt-1">
            {selectedModule
              ? 'Trage pro Lektion eine YouTube-URL ein. Formate: https://youtube.com/watch?v=… · https://youtu.be/… · oder nur die Video-ID.'
              : 'Wähle einen der 10 Micro-Learning-Kurse aus, um YouTube-Videos zuzuordnen.'}
          </DialogDescription>
        </DialogHeader>

        {!selectedModule && (
          <div className="grid gap-2 mt-4" data-testid="video-manager-module-list">
            {MICRO_LEARNING_MODULES.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedModule(m)}
                className="text-left px-4 py-3 rounded-lg border border-white/10 bg-black/30 hover:border-red-500/40 hover:bg-red-500/5 transition-colors flex items-center justify-between"
                data-testid={`video-manager-module-${m.id}`}
              >
                <span className="text-white">{m.title}</span>
                <span className="text-red-400 text-sm">Öffnen →</span>
              </button>
            ))}
          </div>
        )}

        {selectedModule && (
          <div className="mt-4 space-y-3">
            <Button
              variant="ghost"
              onClick={resetView}
              className="text-gray-400 hover:text-white mb-2"
              data-testid="video-manager-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Andere Kurs wählen
            </Button>

            {loading && (
              <div className="text-center py-6 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                Lektionen laden…
              </div>
            )}

            {!loading && lessons.length === 0 && (
              <div className="text-center py-6 text-gray-500 italic">Keine Lektionen gefunden.</div>
            )}

            {!loading && lessons.map((l) => {
              const currentUrl = drafts[l.id] || '';
              const hasUrl = currentUrl.trim().length > 0;
              return (
                <div
                  key={l.id}
                  className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2"
                  data-testid={`video-manager-lesson-${l.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        <span className="text-red-400 font-mono text-xs mr-2">#{l.order}</span>
                        {l.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {l.lesson_type} · {l.duration_minutes} Min · Wissens-Check: {l.knowledge_checks_count}
                      </p>
                    </div>
                    {hasUrl && (
                      <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={currentUrl}
                      onChange={(e) => setDrafts({ ...drafts, [l.id]: e.target.value })}
                      placeholder="https://youtube.com/watch?v=... oder nur ID"
                      className="bg-black/50 border-white/20 text-white text-sm"
                      data-testid={`video-manager-input-${l.id}`}
                    />
                    {hasUrl && (
                      <a
                        href={currentUrl.startsWith('http') ? currentUrl : `https://www.youtube.com/watch?v=${currentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-400 hover:text-red-300 flex-shrink-0"
                        title="Video öffnen (neuer Tab)"
                        data-testid={`video-manager-open-${l.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}

            {!loading && lessons.length > 0 && (
              <div className="flex justify-end pt-4 border-t border-white/10">
                <Button
                  onClick={handleSaveAll}
                  disabled={saving}
                  className="bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold hover:from-red-500 hover:to-red-400"
                  data-testid="video-manager-save-btn"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichere…</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> Alle Video-URLs speichern</>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LessonVideoUrlManager;
